import React from 'react';
import { usePage, router } from '@inertiajs/react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Calendar, Users, Clock, CheckSquare, LogOut, Lock } from 'lucide-react';

interface NavigationItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  admin?: boolean;
  manager?: boolean;
}

interface SidebarProps {
  user: {
    name: string;
    email: string;
    is_admin: boolean;
    is_manager: boolean;
    avatar_image_url: string | null;
    avatar_fallback: string;
  };
  routes: {
    root: string;
    travels_index: string;
    travels_new: string;
    travels_create: string;
    sessions_new: string;
    sessions_create: string;
    sessions_destroy: string;
    approvals_index: string;
    people_index: string;
  };
}

export const Sidebar: React.FC<SidebarProps> = ({ user, routes }) => {
  const { url } = usePage();

  const dynamicNavigationItems: NavigationItem[] = [
    {
      title: 'Travel Timeline',
      href: routes.travels_index,
      icon: Calendar,
    },
    {
      title: 'Request Travel',
      href: routes.travels_new,
      icon: Clock,
    },
    {
      title: 'Approval Dashboard',
      href: routes.approvals_index,
      icon: CheckSquare,
      manager: true,
    },
    {
      title: 'Employee Management',
      href: routes.people_index,
      icon: Users,
      admin: true,
    },
  ]

  const filteredItems = dynamicNavigationItems.filter(item => {
    if (item.admin && !user.is_admin) return false;
    if (item.manager && !user.is_manager) return false;
    return true;
  });

  return (
    <div className="group h-screen w-64 bg-gray-50 flex flex-col border-r border-gray-100">
      <div className="px-3 py-4 flex flex-col h-full">
        <div className="mb-8 px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Travel Portal</h2>
          <p className="text-sm text-gray-600">Company Travel Management</p>
        </div>

        <div className="space-y-1">
          {filteredItems.map((item) => {
            const isActive = url === item.href;
            const Icon = item.icon;

            return (
              <Button
                key={item.href}
                variant="ghost"
                className={`w-full justify-start gap-2 font-normal ${
                  isActive ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => window.location.href = item.href}
              >
                <Icon className="h-4 w-4" />
                {item.title}
                {item.admin && <Lock className="h-3 w-3 ml-auto text-gray-400" />}
              </Button>
            );
          })}
        </div>

        <div className="mt-auto space-y-2">
          <div className="flex items-center gap-3 px-3 py-2 mx-3 rounded-lg hover:bg-gray-100">
            <Avatar className="h-8 w-8 shrink-0 rounded-lg">
              <AvatarImage src={user.avatar_image_url ?? undefined} alt={user.name} />
              <AvatarFallback>
                {user.avatar_fallback}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>
          </div>

          <Button
            variant="ghost"
            type="submit"
            className="w-full justify-start gap-2 font-normal text-gray-600 hover:bg-gray-100"
            onClick={() => router.delete(routes.sessions_destroy)}
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
};
